import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, ErrorBanner, Loader, PageHeader, StatusBadge } from "@/components/common";
import { fmtKm } from "@/lib/format";
import { FRONT_OFFICE } from "@/lib/i18n";
import { VehicleForm } from "@/components/VehicleForm";

export default function VehiclesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const { data, loading, error, reload } = useFetch(() => api.get("/vehicles", { params: { q } }), [q]);
  return (
    <div data-testid="vehicles-page">
      <PageHeader title="Véhicules" subtitle="Immatriculation, VIN, kilométrage et dossier en cours" actions={FRONT_OFFICE.includes(user.role) && <Button onClick={() => setOpen(true)} className="rounded-full" data-testid="vehicles-new-button"><Plus className="mr-2 h-4 w-4" /> Nouveau véhicule</Button>} />
      <div className="relative mb-4 max-w-md"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Plaque, VIN, marque, modèle…" className="h-11 rounded-full pl-10" data-testid="vehicles-search-input" /></div>
      {loading && !data ? <Loader /> : error ? <ErrorBanner message={error} onRetry={reload} /> : !data.length ? <EmptyState title="Aucun véhicule" /> : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {data.map((v, i) => (
            <button key={v.id} onClick={() => navigate(`/vehicules/${v.id}`)} style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }} className="surface surface-hover rise flex flex-col gap-2 p-4 text-left" data-testid={`vehicle-card-${v.plate}`}>
              <div className="flex items-start justify-between gap-2"><span className="rounded-lg border-2 border-foreground/80 bg-background px-2 py-0.5 font-mono text-sm font-bold tracking-wider">{v.plate}</span>{v.open_status && <StatusBadge status={v.open_status} />}</div>
              <div><div className="text-base font-semibold">{v.brand} {v.model}</div><div className="text-xs text-muted-foreground">{v.version} · {v.year} · {v.fuel}</div></div>
              <div className="flex items-center justify-between text-xs text-muted-foreground"><span>{v.client_name}</span><span className="tabular">{fmtKm(v.last_mileage)}</span></div>
              <div className="truncate font-mono text-[10px] text-muted-foreground">VIN {v.vin || "n/c"}</div>
            </button>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}><DialogContent className="max-w-2xl"><DialogHeader><DialogTitle>Nouveau véhicule</DialogTitle></DialogHeader><VehicleForm onSaved={(v) => { setOpen(false); navigate(`/vehicules/${v.id}`); }} /></DialogContent></Dialog>
    </div>
  );
}
